import React from 'react';
import { Box, Typography, Alert, Link, Chip } from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import { Announcement } from '../../types/Event';

interface AnnouncementsListProps {
  announcements: Announcement[];
  isParticipant?: boolean;
}

// Function to convert URLs in text to clickable links
const renderTextWithLinks = (text: string) => {
  // Regular expression to match URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  
  return parts.map((part, index) => {
    if (part.match(urlRegex)) {
      return (
        <Link
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ 
            color: 'primary.main',
            textDecoration: 'underline',
            wordBreak: 'break-all',
            '&:hover': {
              textDecoration: 'underline',
              opacity: 0.8
            }
          }}
        >
          {part}
        </Link>
      );
    }
    return part;
  });
};

const AnnouncementsList: React.FC<AnnouncementsListProps> = ({ announcements, isParticipant = false }) => {
  // Filter announcements based on visibility and user participation status
  const visibleAnnouncements = announcements.filter(announcement => {
    // If visibility is not set or is public, show it to everyone
    if (!announcement.visibility || announcement.visibility === 'public') {
      return true;
    }
    // If visibility is private, only show to participants
    if (announcement.visibility === 'private') {
      return isParticipant;
    }
    return true;
  });

  if (!visibleAnnouncements || visibleAnnouncements.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <CampaignIcon color="primary" />
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          活動公告
        </Typography>
      </Box>
      
      {visibleAnnouncements.map((announcement) => (
        <Alert
          key={announcement._id}
          severity={announcement.isImportant ? 'error' : 'info'}
          icon={<CampaignIcon />}
          sx={{ mb: 2 }}
        >
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                {announcement.title}
              </Typography>
              {announcement.isImportant && (
                <Chip label="重要" color="error" size="small" />
              )}
            </Box>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
              {renderTextWithLinks(announcement.message)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {new Date(announcement.createdAt).toLocaleString('zh-TW')}
            </Typography>
          </Box>
        </Alert>
      ))}
    </Box>
  );
};

export default AnnouncementsList;
